import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { Button, Stack, useMediaQuery } from '@mui/material';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { Table } from '.';
import circlePlusIcon from '../../../assets/images/gray-bg-black-plus.svg';
import { EllipsisTooltip } from './EllipsisTooltip';
import { Dialog } from './Dialog';

const StyledSpan = styled.span`
  display: block;
  padding: 1rem 0;
`;

const RowActions = ({
  rowIndex,
  openEditModal,
  disableRemoval,
  largeScreen,
  openDeleteModal,
}) => (
  <>
    <Stack
      direction="row"
      sx={{
        '@media (min-width: 800px)': { justifyContent: 'end' },
        '&&& > *': {
          margin: '0 0.5rem',
          minWidth: 'unset',
          padding: 0,
        },
      }}
    >
      <Button variant="text" onClick={() => openEditModal(rowIndex)}>
        Edit
      </Button>
      <Button
        data-tip="Please call us at 866-553-2400 to remove this person."
        data-for={`remove=${rowIndex}`}
        variant="text"
        color={disableRemoval ? 'disabled' : 'neutral'}
        sx={{
          '&& svg': {
            color: disableRemoval ? 'disabled.main' : 'neutral.main',
          },
          '&& svg:hover': {
            color: disableRemoval ? 'disabled.main' : 'error.main',
          },
        }}
        onClick={() => {
          /*
          This is a bit of a hack, I would've preferred to use the `disable` prop on the button but we
          wouldn't be able to see the tooltip text b/c the disable prop will set 'pointer-events: none'
          and the hover triggering the tooltip reveal reveak will not be detected.
        */
          if (disableRemoval) return;

          openDeleteModal();
        }}
      >
        <ReactTooltip
          id={`remove=${rowIndex}`}
          place="top"
          effect="solid"
          disable={!disableRemoval}
          className="info-tooltip"
          offset={30}
        />
        {largeScreen ? <FontAwesomeIcon icon={faXmark} size="lg" /> : 'Delete'}
      </Button>
    </Stack>
  </>
);

export default function ClinicPersonnelTable({
  people,
  removePerson,
  openModal,
  disableAddingNew,
  columns = [],
  payToPractice,
  loggedInEmail,
  displayActions = true,
}) {
  const [deleteIndex, setDeleteIndex] = React.useState(null);
  const icon = (
    <img src={circlePlusIcon} width="30" height="30" alt="plus icon" />
  );

  const largeScreen = useMediaQuery('(min-width:800px)');

  const personToDelete = people[deleteIndex];

  const numberOfOwners = people.filter((person) =>
    person.practiceRoles.includes('owner')
  ).length;

  return (
    <Table.Container>
      <Table>
        <Table.TableHead>
          <Table.TableRow>
            {columns.map((column, index) => (
              <Table.TableCell
                key={index}
                align={column.align}
                className={column.className}
              >
                <>{column.label}</>
              </Table.TableCell>
            ))}
          </Table.TableRow>
        </Table.TableHead>
        <Table.TableBody>
          {Object.keys(people).map((index) => {
            const intIndex = parseInt(index);
            const person = people[intIndex];

            const disableRemoval =
              !!person.id &&
              ((!payToPractice &&
                person.practiceRoles.includes('veterinarian')) ||
                (person.practiceRoles.includes('owner') &&
                  numberOfOwners <= 1) ||
                person.email === loggedInEmail ||
                people.length === 1);

            return (
              <Table.TableRow key={index}>
                {columns
                  .filter(({ cellKey }) => cellKey !== 'actions')
                  .map((column) => (
                    <Table.TableCell
                      data-label={column.label}
                      key={column.cellKey}
                      align={column.align}
                      className={column.className}
                    >
                      {column.includeEllipsis ? (
                        <EllipsisTooltip id={`${column.cellKey}-${index}`}>
                          <div>
                            {column.CellComponent ? (
                              <column.CellComponent person={person} />
                            ) : (
                              person[column.cellKey]
                            )}
                          </div>
                        </EllipsisTooltip>
                      ) : (
                        <>
                          {column.CellComponent ? (
                            <column.CellComponent person={person} />
                          ) : (
                            person[column.cellKey]
                          )}
                        </>
                      )}
                    </Table.TableCell>
                  ))}
                {displayActions && (
                  <Table.RowActionsCell data-label="Actions" align="center">
                    <RowActions
                      disableRemoval={disableRemoval}
                      row={person}
                      person={person}
                      rowIndex={intIndex}
                      openEditModal={() =>
                        openModal({ mode: 'edit', editingIndex: intIndex })
                      }
                      largeScreen={largeScreen}
                      openDeleteModal={() => {
                        setDeleteIndex(intIndex);
                      }}
                    />
                  </Table.RowActionsCell>
                )}
              </Table.TableRow>
            );
          })}
          <Table.TableActionsRow>
            <Table.TableCell colSpan={12} align="left">
              {disableAddingNew ? (
                <StyledSpan>
                  Need to add someone? If so, please call us at
                  <a href="tel:866-553-2400"> 866-553-2400.</a>
                </StyledSpan>
              ) : (
                <Button
                  startIcon={icon}
                  onClick={() => openModal({ mode: 'add', editingIndex: null })}
                >
                  Add Person
                </Button>
              )}
            </Table.TableCell>
          </Table.TableActionsRow>
        </Table.TableBody>
      </Table>
      {personToDelete && (
        <Dialog
          id={personToDelete.id}
          open={deleteIndex !== null}
          onClose={() => setDeleteIndex(null)}
          header={`Remove ${personToDelete.firstName} ${personToDelete.lastName}`}
          actions={
            <>
              <Button
                type="submit"
                fullWidth
                color="error"
                variant="contained"
                onClick={() => {
                  removePerson({
                    index: deleteIndex,
                    id: personToDelete.id,
                  });
                  setDeleteIndex(null);
                }}
              >
                Remove
              </Button>
              <Button
                onClick={() => setDeleteIndex(null)}
                fullWidth
                color="primary"
                variant="text"
              >
                Cancel
              </Button>
            </>
          }
        >
          <div>
            Are you sure you want to remove {personToDelete.firstName}{' '}
            {personToDelete.lastName} from this practice?
          </div>
        </Dialog>
      )}
    </Table.Container>
  );
}
